import http from '@/api/httpRequest.js';
import axios from 'axios';
import { normalizeMediaPath } from './mediaUrl.js';

/** presigned 上传文件类型：1 图片，2 文件，4 视频 */
export const PRESIGNED_FILE_TYPE = {
	IMAGE: 1,
	FILE: 2,
	VIDEO: 4
};

const THUMB_MAX_EDGE = 200;

/**
 * 从 uploadItems 中按 uploadType 取 objectKey（相对路径）
 */
function findUploadItemObjectKey(uploadItems, types) {
	if (!Array.isArray(uploadItems)) return null;
	const typeList = (Array.isArray(types) ? types : [types]).map((t) => String(t).toLowerCase());
	const item = uploadItems.find((i) => typeList.includes(String(i.uploadType || '').toLowerCase()));
	if (!item || !item.objectKey) return null;
	return normalizeMediaPath(item.objectKey);
}

/**
 * 将 presigned 接口返回的数据转为消息/头像等业务使用的图片对象（URL 字段为相对路径 objectKey）
 */
export function buildImageUploadPayload(presignedData) {
	if (!presignedData || typeof presignedData !== 'object') {
		return presignedData;
	}
	const urls = presignedData.urls || {};
	const uploadItems = presignedData.uploadItems || [];
	const originUrl = findUploadItemObjectKey(uploadItems, ['origin'])
		|| normalizeMediaPath(urls.origin || presignedData.originUrl);
	const thumbUrl = findUploadItemObjectKey(uploadItems, ['thumb'])
		|| normalizeMediaPath(urls.thumb || presignedData.thumbUrl);
	return {
		originUrl,
		thumbUrl,
		imageId: urls.originId || presignedData.imageId,
		thumbId: urls.thumbId,
		originalFinished: presignedData.originalFinished,
		width: presignedData.width,
		height: presignedData.height,
		fileType: presignedData.fileType
	};
}

/**
 * 将 presigned 接口返回的数据转为普通文件业务对象（url 为相对路径 objectKey）
 */
export function buildFileUploadPayload(presignedData) {
	if (!presignedData || typeof presignedData !== 'object') {
		return presignedData;
	}
	const urls = presignedData.urls || {};
	const uploadItems = presignedData.uploadItems || [];
	const url = findUploadItemObjectKey(uploadItems, ['origin', 'file'])
		|| normalizeMediaPath(urls.origin || urls.file || urls.url || presignedData.url);
	return { url };
}

export function buildPresignedUploadPayload(presignedData, fileType) {
	if (fileType === PRESIGNED_FILE_TYPE.FILE) {
		return buildFileUploadPayload(presignedData);
	}
	if (fileType === PRESIGNED_FILE_TYPE.VIDEO) {
		return buildVideoUploadPayload(presignedData);
	}
	return buildImageUploadPayload(presignedData);
}

/**
 * 将 presigned 接口返回的数据转为视频消息业务对象（URL 字段为相对路径 objectKey）
 */
export function buildVideoUploadPayload(presignedData) {
	if (!presignedData || typeof presignedData !== 'object') {
		return presignedData;
	}
	const urls = presignedData.urls || {};
	const uploadItems = presignedData.uploadItems || [];
	const videoUrl = findUploadItemObjectKey(uploadItems, ['video', 'origin'])
		|| normalizeMediaPath(urls.video || urls.origin || presignedData.videoUrl);
	const coverUrl = findUploadItemObjectKey(uploadItems, ['cover', 'thumb'])
		|| normalizeMediaPath(urls.cover || urls.thumb || presignedData.coverUrl);
	return {
		videoUrl,
		coverUrl
	};
}

/**
 * 申请 presigned 上传凭证
 * @param {{ fileName: string, fileSize: number, contentType?: string, fileType: number }} params
 */
export function fetchImagePresigned(params) {
	return http({
		url: '/image/upload/presigned',
		method: 'post',
		data: {
			fileName: params.fileName,
			fileSize: params.fileSize,
			contentType: params.contentType,
			fileType: params.fileType
		},
	});
}

/**
 * 生成图片缩略图 Blob
 */
function createImageThumbBlob(file, contentType = 'image/jpeg') {
	return new Promise((resolve, reject) => {
		const img = new Image();
		const objectUrl = URL.createObjectURL(file);
		img.onload = () => {
			URL.revokeObjectURL(objectUrl);
			let width = img.width;
			let height = img.height;
			const maxEdge = Math.max(width, height);
			if (maxEdge > THUMB_MAX_EDGE) {
				const scale = THUMB_MAX_EDGE / maxEdge;
				width = Math.round(width * scale);
				height = Math.round(height * scale);
			}
			const canvas = document.createElement('canvas');
			canvas.width = width;
			canvas.height = height;
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				reject(new Error('缩略图生成失败'));
				return;
			}
			ctx.drawImage(img, 0, 0, width, height);
			canvas.toBlob((blob) => {
				if (blob) {
					resolve(blob);
				} else {
					reject(new Error('缩略图生成失败'));
				}
			}, contentType || 'image/jpeg', 0.85);
		};
		img.onerror = () => {
			URL.revokeObjectURL(objectUrl);
			reject(new Error('图片加载失败'));
		};
		img.src = objectUrl;
	});
}

/**
 * 从视频中截取封面帧
 */
function createVideoCoverBlob(file, contentType = 'image/jpeg') {
	return new Promise((resolve, reject) => {
		const video = document.createElement('video');
		video.preload = 'auto';
		video.muted = true;
		video.playsInline = true;
		const objectUrl = URL.createObjectURL(file);
		const cleanup = () => URL.revokeObjectURL(objectUrl);

		video.onloadeddata = () => {
			const seekTime = Number.isFinite(video.duration) && video.duration > 0
				? Math.min(0.1, video.duration / 2)
				: 0;
			video.currentTime = seekTime;
		};
		video.onseeked = () => {
			try {
				const width = video.videoWidth;
				const height = video.videoHeight;
				if (!width || !height) {
					cleanup();
					reject(new Error('视频封面生成失败'));
					return;
				}
				const canvas = document.createElement('canvas');
				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					cleanup();
					reject(new Error('视频封面生成失败'));
					return;
				}
				ctx.drawImage(video, 0, 0, width, height);
				canvas.toBlob((blob) => {
					cleanup();
					if (blob) {
						resolve(blob);
					} else {
						reject(new Error('视频封面生成失败'));
					}
				}, contentType || 'image/jpeg', 0.85);
			} catch (e) {
				cleanup();
				reject(e);
			}
		};
		video.onerror = () => {
			cleanup();
			reject(new Error('视频加载失败'));
		};
		video.src = objectUrl;
	});
}

/**
 * 根据 uploadItem 类型解析待上传内容
 */
async function resolveUploadBlob(file, uploadItem) {
	const uploadType = String(uploadItem.uploadType || '').toLowerCase();
	const contentType = uploadItem.contentType
		|| (uploadItem.headers && (uploadItem.headers['Content-Type'] || uploadItem.headers['content-type']))
		|| 'image/jpeg';

	if (uploadType === 'thumb' || uploadType === 'cover') {
		if (file.type && file.type.startsWith('image/')) {
			return createImageThumbBlob(file, contentType);
		}
		if (file.type && file.type.startsWith('video/')) {
			return createVideoCoverBlob(file, contentType);
		}
	}
	return file;
}

/**
 * 使用 presigned 凭证直传
 * 图片/文件：PUT 二进制（现有逻辑）
 * 视频：对齐 uni.uploadFile —— FormData，name='file'，并附带 fields
 */
export async function uploadFileToPresignedUrl(file, presigned, options = {}) {
	const { onUploadProgress, signal, useFormData = false, fileName } = options;
	const uploadUrl = presigned.uploadUrl;
	if (!uploadUrl) {
		throw new Error('缺少 uploadUrl');
	}

	// 视频：对齐 uni.uploadFile({ name: 'file', formData: fields })
	if (useFormData && presigned.uploadType != 'cover') {
		const formData = new FormData();
		const fields = (presigned && typeof presigned.fields === 'object' && presigned.fields)
			? presigned.fields
			: {};
		Object.keys(fields).forEach((key) => {
			const value = fields[key];
			if (value !== undefined && value !== null) {
				formData.append(key, value);
			}
		});
		const name = fileName || (file && file.name) || 'file';
		formData.append('file', file, name);
		await axios({
			url: uploadUrl,
			method: (presigned.method || 'POST').toUpperCase(),
			data: formData,
			timeout: 60 * 1000 * 5,
			onUploadProgress,
		});
		return;
	}

	// 图片/文件：原 PUT 直传
	const method = (presigned.method || 'PUT').toUpperCase();
	const headers = {};
	const rawHeaders = presigned.headers || {};
	Object.keys(rawHeaders).forEach((key) => {
		const value = rawHeaders[key];
		if (value !== undefined && value !== null && value !== '') {
			headers[key] = value;
		}
	});

	const contentType = presigned.contentType
		|| (file && file.type)
		|| headers['Content-Type']
		|| headers['content-type'];
	const hasContentType = Object.keys(headers).some((key) => key.toLowerCase() === 'content-type');
	if (!hasContentType && contentType) {
		headers['Content-Type'] = contentType;
	}

	await axios({
		url: uploadUrl,
		method,
		data: file,
		headers,
		onUploadProgress,
		signal,
		transformRequest: [(data) => data],
		maxBodyLength: Infinity,
		maxContentLength: Infinity,
		needThumb: 1
	});
}

/**
 * 按 uploadItems 逐项上传
 */
async function uploadPresignedItems(file, presignedData, options = {}) {
	const uploadItems = Array.isArray(presignedData.uploadItems) ? presignedData.uploadItems : [];
	if (!uploadItems.length) {
		throw new Error('缺少 uploadItems');
	}

	const useFormData = options.fileType === PRESIGNED_FILE_TYPE.VIDEO;
	const blobs = await Promise.all(uploadItems.map((item) => resolveUploadBlob(file, item)));
	const totalBytes = blobs.reduce((sum, blob) => sum + (blob.size || 0), 0);
	let finishedBytes = 0;

	for (let i = 0; i < uploadItems.length; i++) {
		const item = uploadItems[i];
		const blob = blobs[i];
		await uploadFileToPresignedUrl(blob, item, {
			signal: options.signal,
			useFormData,
			fileName: file.name,
			onUploadProgress: (progressEvent) => {
				if (!options.onUploadProgress) {
					return;
				}
				const itemLoaded = Number(progressEvent.loaded) || 0;
				options.onUploadProgress({
					...progressEvent,
					loaded: finishedBytes + itemLoaded,
					total: totalBytes || progressEvent.total || file.size
				});
			}
		});
		finishedBytes += blob.size || 0;
		if (options.onUploadProgress) {
			options.onUploadProgress({
				loaded: finishedBytes,
				total: totalBytes || file.size
			});
		}
	}
}

/**
 * 完整流程：申请 presigned -> 按 uploadItems 直传 S3 -> 返回业务数据
 * @param {File|Blob} file
 * @param {{ onUploadProgress?: Function, signal?: AbortSignal, fileType?: number }} [options]
 */
export async function uploadViaPresigned(file, options = {}) {
	const { onUploadProgress, signal, fileType = PRESIGNED_FILE_TYPE.IMAGE } = options;
	const defaultNames = {
		[PRESIGNED_FILE_TYPE.IMAGE]: 'image',
		[PRESIGNED_FILE_TYPE.FILE]: 'file',
		[PRESIGNED_FILE_TYPE.VIDEO]: 'video.mp4'
	};
	const fileName = file.name || defaultNames[fileType] || 'file';
	const fileSize = file.size || 0;
	const presignedData = await fetchImagePresigned({
		fileName,
		fileSize,
		contentType: file.type || 'application/octet-stream',
		fileType
	});
	await uploadPresignedItems(file, presignedData, { onUploadProgress, signal, fileType });
	return buildPresignedUploadPayload(presignedData, fileType);
}

/** @deprecated 请使用 uploadViaPresigned */
export async function uploadImageViaPresigned(file, options = {}) {
	return uploadViaPresigned(file, {
		...options,
		fileType: options.fileType ?? PRESIGNED_FILE_TYPE.IMAGE
	});
}

/**
 * 与 returnFullResponse 结构对齐，供 ChatBox 测速/归一化逻辑复用
 */
export function wrapImageUploadFullResponse(payload) {
	return wrapPresignedUploadFullResponse(payload);
}

export function wrapPresignedUploadFullResponse(payload) {
	return {
		data: payload,
		headers: {},
		status: 200
	};
}
